// @flow

import * as React from 'react';
import get from 'lodash/get';

import classNames from '@ta/common.classnames';
import { localize } from '@ta/common.i18n';
import Loadable from '@ta/common.loadable';
import trackEventAJAX from '@ta/common.tracking';
import type { TrackingEventAJAXProps } from '@ta/common.tracking';

// eslint-disable-next-line no-restricted-imports
import { getStore } from '@ta/common.state';
import {
  ResponsiveImage,
  type FitStrategy,
  type BackgroundSizeCalculator,
  type Source as ResponsiveImageSource,
  type ContainerDimensions as ResponsiveImageContainerSize,
} from '@ta/media.image';
import { Icon } from '@ta/styleguide.icon';
import IndicatorDots from '@ta/common.indicator-dots';
import CarouselProgress from './CarouselProgress';
import styles from './PhotoCarousel.less';
import { getState, ACTIONS as PHOTO_CAROUSEL_ACTIONS } from './photoCarouselState';

const SWIPE_THRESHOLD_PERCENT = 0.1;
const LOAD_THRESHOLD = 2;

/** Model for a responsive photo */
export type Photo = {|
  /** Id of the photo */
  id: number,

  /** List of responsive photo sizes */
  photoSizes: Array<ResponsiveImageSource>,
|};

type CallbackArgs = {|
  currentIndex: number,
|};

/** Component props for a <PhotoCarousel> */
export type Props = {|
  /** CSS width property */
  width: string,

  /** CSS height property */
  height: string,

  /** The list of photo sources */
  photos: Array<Photo>,

  /** Total number of photos */
  totalCount: number,

  /** Index of the photo to display */
  displayIndex?: number,

  /** Optional photo click handler */
  onClick?: number => void,

  /** Optional callback for when the carousel displays a new photo */
  onPhotoChange?: (number, ?SyntheticEvent<HTMLDivElement>) => void,

  /** Optional callback for when the carousel loads its last photo */
  onLoadLast?: () => void,

  /** Optional callback for when the photo carousel is moused over */
  onPhotoMouseover?: (isLastLoadedImg: boolean) => void,

  /** Optional callback for when the photo carousel is about to navigate */
  onNav?: (dir: number, isClick: boolean) => void,

  /** Optional prop to show the certificate of excellence badge on the carousel */
  badgeTopLeft?: ?React.Element<any>,

  /** Optional children render prop function (Usually used for photo overlays) */
  children?: CallbackArgs => React.Node,

  /** Set to true to allow clicks on the nav arrow areas when no arrows are present, resulting in the clicks bubbling up to the onClick handler.
   * If this is false, the arrow area on the left and right sides will have clicks disabled when reaching either end of the carousel
   * which prevents users that are quickly clicking through photos from triggering the onClick behavior unintentionally. */
  allowOverClicking?: boolean,

  /** Optional prop to set the height percentage of the photo in case there is need to hide part of the image e.g. watermarks */
  photoHeight?: string,

  /** Hide the overflow portion of photos */
  overflowHidden?: boolean,

  /** Optional prop to control the size of the carousel arrow
   * Default: large */
  arrowSize?: 'large' | 'small',

  /** Optional prop to control the arrow hiding behavior
   * If true, the arrows will only appear when the mouse is hovering over the carousel.
   * If false, the arrows will always display */
  hideArrows?: boolean,

  /** Optional prop to control cta behavior.
   * If true, don't display the "Full view" cta */
  noCta?: boolean,

  /** Optional prop for displaying indicator dots at the bottom of the carousel */
  showIndicatorDots?: boolean,

  /** The maximum number of indicator dots to show at the bottom of the carousel, defaults to 5 (once you reach the end, it slides more indicator dots into view) */
  maxIndicatorDots?: number,

  /** Optional prop: should the carousel photos wrap around from end to beginning */
  wrapPhotos?: boolean,

  /** Should this component save its state in redux so that it can be remembered after re-initialization? If true, you also need to specify a uniqueId */
  saveReduxState?: boolean,

  /** unique id for storing carousel state in redux store */
  uniqueId?: string,

  /** To force server side rendering: ResponsiveImage relies on container width and height
   * to pick the right size image to save bandwidth. However, this can take some time
   * on the client side, sometimes even seconds on slower devices like mobiles.
   * For images that are critical to be loaded as quickly as possible, this option can be set by
   * passing ContainerDimensions to be used as container dimensions on the server side.
   * We will only attempt to ssr if this prop is set and the current carousel index is 0
   */
  ssrContainerDimensions?: ResponsiveImageContainerSize,

  /** Should the carousel images be lazy loaded? This will have no effect if you specify ssrContainerDimensions,
   * because it forces ResponsiveImage to pick and load an image */
  lazyLoadImages?: boolean,

  /**
   * Fit strategy for ResponsiveImage - how should the image be fit within the layout
   * cover - cover the entire layout with the image (default)
   * contain - fit the complete image in the layout
   * calculated - specify a custom fit strategy using backgroundSizeCalculator()
   *  or use the default implementation
   *  'cover' || 'contain' || 'calculated'
   */
  fitStrategy?: FitStrategy,

  /**
   * A custom method for deciding fit, if fitStrategy is set to 'calculated'
   * Should return a valid backgroundSize (contain, cover, xx%, etc;
   * see https://developer.mozilla.org/en-US/docs/Web/CSS/background-size)
   *  */
  backgroundSizeCalculator?: BackgroundSizeCalculator,

  /**
   *  Optional Custom Image Container
   */
  imageContainer?: Photo => React.Node,

  /** should the carousel show the progress (e.g. 1/3) in the top right corner */
  showProgress?: boolean,

  /** if provided will record an ajaxEvent whenever the next button is clicked */
  onNextTrackingProps?: TrackingEventAJAXProps,

  /** if provided will record an ajaxEvent whenever the previous button is clicked */
  onPreviousTrackingProps?: TrackingEventAJAXProps,
|};

type State = {|
  currentIndex: number,
  hasWrapped: boolean,
|};

type SwipeState = {|
  xStart: number | null,
  xLast: number | null,
  swipeThreshold: number | null,
|};

const getCarouselStateFromRedux = (uniqueId: string) => getState(getStore().getState())[uniqueId];

const NavArrow = ({
  isRight,
  size,
  onClick,
  disabled,
  allowOverClicking,
}: {
  isRight: boolean,
  disabled: boolean,
  allowOverClicking: ?boolean,
  size: ?string,
  onClick: (e: SyntheticEvent<*>) => void,
}) => {
  let arrowType;

  if (disabled) {
    if (allowOverClicking) {
      return null;
    }
    arrowType = isRight ? styles.rightNavDisabled : styles.leftNavDisabled;
  } else {
    arrowType = isRight ? styles.rightNav : styles.leftNav;
  }

  const arrowSizeClass = size && size === 'small' ? styles.small : null;

  return (
    <div
      className={classNames(arrowType, arrowSizeClass, 'is-shown-at-tablet')}
      onClick={onClick}
      data-clicksource="CarouselArrow"
    />
  );
};

/**
 * A Photo Carousel, displaying Responsive Images
 */
class PhotoCarousel extends React.Component<Props, State> {
  static getDerivedStateFromProps(
    { displayIndex, totalCount, photos }: Props,
    { currentIndex }: State,
  ) {
    if (displayIndex === undefined) {
      return null;
    }

    const lastIndex = (totalCount || photos.length) - 1;
    if (displayIndex !== currentIndex) {
      return {
        currentIndex: Math.max(Math.min(displayIndex, lastIndex), 0),
      };
    }

    return null;
  }

  constructor(props: Props) {
    super(props);
    let currentIndex = 0;
    let hasWrapped = false;

    const { uniqueId } = this.props;
    if (this.props.saveReduxState && uniqueId) {
      const carouselState = getCarouselStateFromRedux(uniqueId);
      currentIndex = get(carouselState, 'currentIndex', 0);
      hasWrapped = get(carouselState, 'hasWrapped', false);
    }

    this.state = {
      currentIndex,
      hasWrapped,
    };
  }

  componentDidUpdate() {
    const { uniqueId } = this.props;
    if (this.props.saveReduxState && uniqueId) {
      // Save carousel state to to the private redux slice
      getStore().dispatch(
        PHOTO_CAROUSEL_ACTIONS.registerState(
          uniqueId,
          this.state.currentIndex,
          this.state.hasWrapped,
        ),
      );
    }
  }

  swallow = (e: SyntheticEvent<*>) => {
    e.stopPropagation();
  };

  previous = (e: SyntheticEvent<*>) => {
    e.stopPropagation();
    e.preventDefault();
    this.nav(-1, true, e);
    if (this.props.onNextTrackingProps) {
      trackEventAJAX(this.props.onNextTrackingProps);
    }
  };

  next = (e: SyntheticEvent<*>) => {
    e.stopPropagation();
    e.preventDefault();
    this.nav(1, true, e);
    if (this.props.onPreviousTrackingProps) {
      trackEventAJAX(this.props.onPreviousTrackingProps);
    }
  };

  nav = (dir: number, isClick: boolean, e: SyntheticEvent<HTMLDivElement>) => {
    if (this.props.onNav) {
      this.props.onNav(dir, isClick);
    }

    const totalCount = this.props.totalCount || this.props.photos.length;
    const newIndexTemp = this.state.currentIndex + dir;

    // Check to make sure we don't go off the edge
    if (!this.props.wrapPhotos && (newIndexTemp >= totalCount || newIndexTemp < 0)) {
      return;
    }

    let hasWrappedTemp = this.state.hasWrapped;
    if (newIndexTemp >= totalCount && this.props.wrapPhotos) {
      hasWrappedTemp = true;
    }
    let newIndex;
    if (newIndexTemp < 0) {
      if (hasWrappedTemp) {
        newIndex = Math.max(Math.min(totalCount, totalCount + newIndexTemp), 0);
      } else {
        newIndex = 0;
      }
    } else {
      newIndex = Math.max(
        Math.min(totalCount, this.props.wrapPhotos ? newIndexTemp % totalCount : newIndexTemp),
        0,
      );
    }
    if (newIndex !== this.state.currentIndex) {
      const max = this.props.photos.length - 1;
      if (newIndex >= max - LOAD_THRESHOLD && this.props.onLoadLast) {
        this.props.onLoadLast();
      }
      this.setState({
        currentIndex: newIndex,
        hasWrapped: hasWrappedTemp,
      });

      if (this.props.onPhotoChange) {
        this.props.onPhotoChange(newIndex, e);
      }
    }
  };

  swipeState: SwipeState = {
    xStart: null,
    xLast: null,
    swipeThreshold: null,
  };

  touchStart = (e: SyntheticTouchEvent<*>) => {
    // only recognize single finger touch events to avoid conflicts with pinch-to-zoom or other multi-finger gestures.
    if (!e || !e.currentTarget || !e.touches || e.touches.length !== 1) {
      return;
    }

    this.swipeState.swipeThreshold = e.currentTarget.clientWidth * SWIPE_THRESHOLD_PERCENT;
    this.swipeState.xStart = e.touches[0].pageX - e.currentTarget.offsetLeft;
    this.swipeState.xLast = null;
  };

  touchMove = (e: SyntheticTouchEvent<*>) => {
    // only recognize single finger touch events to avoid conflicts with pinch-to-zoom or other multi-finger gestures.
    if (!e || !e.currentTarget || !e.touches || e.touches.length !== 1) {
      return;
    }

    if (this.swipeState.swipeThreshold === null || this.swipeState.xStart === null) {
      return;
    }

    this.swipeState.xLast = e.touches[0].pageX - ((e.currentTarget: any): HTMLElement).offsetLeft;
    const diff = this.swipeState.xStart - this.swipeState.xLast;

    if (this.swipeState.swipeThreshold <= Math.abs(diff)) {
      this.touchEnd(e);
    }
  };

  touchEnd = (e: SyntheticTouchEvent<HTMLDivElement>) => {
    if (
      this.swipeState.swipeThreshold === null ||
      this.swipeState.xLast === null ||
      this.swipeState.xStart === null
    ) {
      this.swipeState.xStart = null;
      this.swipeState.xLast = null;
      return;
    }

    const diff = this.swipeState.xStart - this.swipeState.xLast;

    if (this.swipeState.swipeThreshold <= Math.abs(diff)) {
      this.swipeState.xStart = null;
      this.swipeState.xLast = null;

      // pass either 1 or -1 to nav
      this.nav(diff / Math.abs(diff), false, e);
    }
  };

  render() {
    const { maxIndicatorDots = 5 } = this.props;
    const totalCount = this.props.totalCount || this.props.photos.length;
    const hideArrowClass = this.props.hideArrows ? styles.hideArrows : null;
    const leftNavEnabled = this.state.currentIndex > 0 || this.state.hasWrapped;
    const rightNavEnabled =
      totalCount > 1 && (this.state.currentIndex < totalCount - 1 || this.props.wrapPhotos);
    const showCta = !this.props.noCta;
    const isLastLoadedImg =
      this.state.currentIndex === this.props.photos.length - 1 &&
      this.props.totalCount !== this.props.photos.length;
    const onPhotoMouseover = () => {
      if (this.props.onPhotoMouseover) {
        this.props.onPhotoMouseover(isLastLoadedImg);
      }
    };

    return (
      <div
        className={classNames(styles.carousel, hideArrowClass)}
        style={{ width: this.props.width, height: this.props.height }}
        onClick={() => {
          // It's possible for currentIndex to be beyond the bounds of the current set of photos, i.e. user scrolled past the current batch of photos and we are in the process of fetching additional photos.
          // If this occurs, use the last photo instead.
          const currentPhoto = this.props.photos[
            Math.min(this.state.currentIndex, this.props.photos.length - 1)
          ];

          if (this.props.onClick && currentPhoto) {
            this.props.onClick(currentPhoto.id);
          }
        }}
        onTouchStart={e => {
          this.touchStart(e);
          onPhotoMouseover();
        }}
        onTouchMove={this.touchMove}
        onTouchEnd={this.touchEnd}
        onMouseEnter={onPhotoMouseover}
      >
        <ul className={styles.images}>
          {this.props.photos.map((photo, index) => (
            <li
              key={photo.id}
              className={classNames(
                styles.photo,
                this.props.overflowHidden ? styles.crop : null,
                this.state.currentIndex === index ? styles.current : null,
              )}
            >
              <Loadable shouldLoad={Math.abs(this.state.currentIndex - index) <= LOAD_THRESHOLD}>
                {this.props.imageContainer ? (
                  this.props.imageContainer(photo)
                ) : (
                  <ResponsiveImage
                    sources={photo.photoSizes}
                    width="100%"
                    height={this.props.photoHeight || '100%'}
                    notLazy={!this.props.lazyLoadImages}
                    ssrContainerDimensions={
                      this.state.currentIndex === 0 ? this.props.ssrContainerDimensions : undefined
                    }
                    fitStrategy={this.props.fitStrategy}
                    backgroundSizeCalculator={this.props.backgroundSizeCalculator}
                  />
                )}
              </Loadable>
            </li>
          ))}
        </ul>

        {this.props.children ? (
          <div className={styles.childrenContainer}>
            {this.props.children({ currentIndex: this.state.currentIndex })}
          </div>
        ) : null}

        {showCta ? (
          <React.Fragment>
            <div className={styles.ctaWrap}>
              <span className={styles.cta}>
                <Icon name="expand" /> {localize('full_view') /* Full view */}
              </span>
            </div>
            <div className={styles.washout} />
          </React.Fragment>
        ) : null}

        {leftNavEnabled ? (
          <NavArrow
            isRight={false}
            size={this.props.arrowSize}
            onClick={this.previous}
            disabled={false}
            allowOverClicking={this.props.allowOverClicking}
          />
        ) : (
          <NavArrow
            isRight={false}
            size={this.props.arrowSize}
            onClick={this.swallow}
            disabled
            allowOverClicking={this.props.allowOverClicking}
          />
        )}
        {rightNavEnabled ? (
          <NavArrow
            isRight
            size={this.props.arrowSize}
            onClick={this.next}
            disabled={false}
            allowOverClicking={this.props.allowOverClicking}
          />
        ) : (
          <NavArrow
            isRight
            size={this.props.arrowSize}
            onClick={this.swallow}
            disabled
            allowOverClicking={this.props.allowOverClicking}
          />
        )}

        {this.props.badgeTopLeft && (
          <div className={styles['badge-container']}>{this.props.badgeTopLeft}</div>
        )}

        {this.props.showIndicatorDots && totalCount ? (
          <IndicatorDots
            numDots={Math.min(maxIndicatorDots, totalCount)}
            numItems={totalCount}
            currentIndex={this.state.currentIndex}
          />
        ) : null}

        {this.props.showProgress && (
          <CarouselProgress currentIndex={this.state.currentIndex + 1} numItems={totalCount} />
        )}
      </div>
    );
  }
}

export default PhotoCarousel;
